/**
 * useAdminBlogList Hook
 * 
 * Custom hook for fetching admin blog list (including drafts).
 */

import { useState, useEffect, useCallback } from 'react';
import { adminBlogService } from '../services/adminBlogService';
import type { BlogPost } from '../types/blog.types';
import { ApiError } from '../services/apiClient';

/**
 * Hook return type
 */
export interface UseAdminBlogListReturn {
  /** Array of blog posts */
  blogs: BlogPost[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  /** Function to refetch blog list */
  refetch: () => Promise<void>;
  /** Delete a blog post */
  deleteBlog: (id: string) => Promise<void>;
}

/**
 * Custom hook for admin blog list
 */
export const useAdminBlogList = (options?: {
  includeDrafts?: boolean;
  search?: string;
}): UseAdminBlogListReturn => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBlogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await adminBlogService.getBlogList(options);
      setBlogs(data);
    } catch (err) {
      const errorMessage =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Failed to fetch blog posts';
      setError(errorMessage);
      setBlogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  const deleteBlog = useCallback(
    async (id: string): Promise<void> => {
      try {
        await adminBlogService.deleteBlog(id);
        // Remove from local state
        setBlogs((prev) => prev.filter((blog) => blog._id !== id));
      } catch (err) {
        const errorMessage =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
            ? err.message
            : 'Failed to delete blog post';
        throw new Error(errorMessage);
      }
    },
    []
  );

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  return {
    blogs,
    isLoading,
    error,
    refetch: fetchBlogs,
    deleteBlog,
  };
};

